import { Routes } from '@angular/router';

import { LayoutPagesComponent } from '@components/layout/layout-pages/layout-pages.component';
import { LayoutPublicPagesComponent } from '@components/layout/layout-public-pages/layout-public-pages.component';

import { authGuard } from '@guards/auth.guard';
import { notStandaloneModeGuard } from '@guards/not-standalone-mode.guard';
import { standaloneModeGuard } from '@guards/standalone-mode.guard';

function getDefaultPage(): string {
    if (window.matchMedia('(display-mode: standalone)').matches) {
        return 'home';
    } else {
        return 'training';
    }
}

export const routes: Routes = [
    { path: '', redirectTo: getDefaultPage(), pathMatch: 'full' },
    {
        path: 'maintenance',
        loadComponent: () => import('./features/public/maintenance/maintenance.component').then((m) => m.MaintenanceComponent),
    },
    {
        path: '',
        component: LayoutPublicPagesComponent,
        children: [
            {
                path: 'install-app',
                canActivate: [notStandaloneModeGuard],
                loadComponent: () => import('./features/public/download-pwa/download-pwa.component').then((m) => m.DownloadPwaComponent),
                data: { header: { mode: 'hamburger' }, title: "Installa l'app | Maria Sole Barbieri" },
            },
        ],
    },
    {
        path: '',
        component: LayoutPagesComponent,
        children: [
            {
                path: 'password-recover',
                loadComponent: () =>
                    import('./features/auth/password-recover/password-recover.component').then((m) => m.PasswordRecoverComponent),
                data: { header: { mode: 'hamburger' }, title: 'Recupera password | Maria Sole Barbieri' },
            },
            {
                path: 'password-reset',
                loadComponent: () =>
                    import('./features/auth/password-reset/password-reset.component').then((m) => m.PasswordResetComponent),
                data: { header: { mode: 'hamburger' }, title: 'Reimposta password | Maria Sole Barbieri' },
            },
            {
                path: 'email-validation',
                loadComponent: () =>
                    import('./features/auth/email-validation/email-validation.component').then((m) => m.EmailValidationComponent),
                data: { header: { mode: 'hamburger' }, title: 'Email confermata | Maria Sole Barbieri' },
            },
        ],
    },
    {
        path: '',
        component: LayoutPagesComponent,
        children: [
            {
                path: '',
                canActivate: [standaloneModeGuard],
                children: [
                    {
                        path: 'home',
                        loadComponent: () => import('./features/standalone/home/home.component').then((m) => m.HomeComponent),
                        data: { header: { mode: 'hamburger' }, title: 'Home | Maria Sole Barbieri', wpPath: '' },
                    },
                    {
                        path: 'programs',
                        loadComponent: () => import('./features/standalone/programs/programs.component').then((m) => m.ProgramsComponent),
                        data: { header: { mode: 'hamburger' }, title: 'Programmi | Maria Sole Barbieri', wpPath: 'programs-woman' },
                    },
                    {
                        path: 'shop',
                        loadComponent: () => import('./features/standalone/shop/shop.component').then((m) => m.ShopComponent),
                        data: { header: { mode: 'hamburger' }, title: 'Shop | Maria Sole Barbieri', wpPath: 'shop' },
                    },
                    {
                        path: 'login',
                        loadComponent: () => import('./features/auth/login/login.component').then((m) => m.LoginComponent),
                        data: { header: { mode: 'hamburger' }, wpPath: 'my-account' },
                    },
                ],
            },
            {
                path: 'faq',
                loadComponent: () => import('./features/public/faq/faq.component').then((m) => m.FaqComponent),
                data: { header: { mode: 'hamburger' }, title: 'FAQ | Maria Sole Barbieri' },
            },
            {
                path: 'msb4you',
                loadComponent: () =>
                    import('./features/public/msb4you/msb4you-categories/msb4you-categories.component').then(
                        (m) => m.Msb4youCategoriesComponent,
                    ),
                data: { header: { mode: 'hamburger' }, title: 'MSB4You | Maria Sole Barbieri' },
            },
            {
                path: 'msb4you/:id',
                loadComponent: () =>
                    import('./features/public/msb4you/msb4you-category-videos/msb4you-category-videos.component').then(
                        (m) => m.Msb4youCategoryVideosComponent,
                    ),
                data: { header: { mode: 'back-history' }, title: 'MSB4You | Maria Sole Barbieri' },
            },
            {
                path: 'msb4you/:id/video/:videoId',
                loadComponent: () =>
                    import('./features/public/msb4you/msb4you-category-video-details/msb4you-category-video-details.component').then(
                        (m) => m.Msb4youCategoryVideoDetailsComponent,
                    ),
                data: { header: { mode: 'back-history' }, title: 'MSB4You | Maria Sole Barbieri' },
            },
        ],
    },
    {
        path: '',
        component: LayoutPagesComponent,
        canActivate: [authGuard],
        children: [
            {
                path: 'training',
                loadComponent: () => import('./features/my-area/my-area.component').then((m) => m.MyAreaComponent),
            },
            {
                path: 'training/month/:number',
                data: { header: { mode: 'back-fixed', backPath: '/training' } },
                loadComponent: () =>
                    import('./features/my-area/training/home/home-videos/home-videos.component').then((m) => m.HomeVideosComponent),
            },
            {
                path: 'training/video/:id',
                data: { isFreeVideo: false, header: { mode: 'back-history' } },
                loadComponent: () =>
                    import('./features/my-area/training/home/home-video-details/home-video-details.component').then(
                        (m) => m.HomeVideoDetailsComponent,
                    ),
            },
            {
                path: 'training/video/:id/free',
                data: { isFreeVideo: true, header: { mode: 'back-history' } },
                loadComponent: () =>
                    import('./features/my-area/training/home/home-video-details/home-video-details.component').then(
                        (m) => m.HomeVideoDetailsComponent,
                    ),
            },
            {
                path: 'my-account/purchase-history',
                data: { header: { mode: 'back-history' }, title: 'I miei acquisti | Maria Sole Barbieri' },
                loadComponent: () =>
                    import('./features/my-area/profile/purchase-history/purchase-history.component').then(
                        (m) => m.PurchaseHistoryComponent,
                    ),
            },
            {
                path: 'nutrition/meal-detail/:typeId',
                data: { header: { mode: 'back-history' }, title: 'Dettaglio pasto | Maria Sole Barbieri' },
                loadComponent: () =>
                    import('./features/my-area/nutrition/nutrition-meal-detail/nutrition-meal-detail.component').then(
                        (m) => m.NutritionMealDetailComponent,
                    ),
            },
            {
                path: 'progress-update-measures/:action/:measureKey',
                data: { header: { mode: 'back-history' }, title: 'Misurazione | Maria Sole Barbieri' },
                loadComponent: () =>
                    import('./features/my-area/progress/progress-update-measures/progress-update-measures.component').then(
                        (m) => m.ProgressUpdateMeasuresComponent,
                    ),
            },
            {
                path: 'gym-program/:programId',
                data: {
                    gymPlanType: 'standard',
                    header: { mode: 'back-fixed', backPath: '/training?trainingTab=gym' },
                    title: 'Programma Gym | Maria Sole Barbieri',
                },
                loadComponent: () =>
                    import('./features/my-area/training/gym/gym-program/gym-program.component').then((m) => m.GymProgramComponent),
            },
            {
                path: 'pregnancy-program/:programId',
                data: {
                    gymPlanType: 'pregnancy_rookie',
                    header: { mode: 'back-fixed', backPath: '/training?trainingTab=pregnancy' },
                    title: 'Programma Gravidanza | Maria Sole Barbieri',
                },
                loadComponent: () =>
                    import('./features/my-area/training/gym/gym-program/gym-program.component').then((m) => m.GymProgramComponent),
            },
            {
                path: 'postpartum-program/:programId',
                data: {
                    gymPlanType: 'postpartum',
                    header: { mode: 'back-fixed', backPath: '/training?trainingTab=pregnancy' },
                    title: 'Programma Post Partum | Maria Sole Barbieri',
                },
                loadComponent: () =>
                    import('./features/my-area/training/gym/gym-program/gym-program.component').then((m) => m.GymProgramComponent),
            },
        ],
    },
    {
        path: 'gym-stories/:programId/:cardId',
        canActivate: [authGuard],
        data: { gymPlanType: 'standard' },
        loadComponent: () => import('./features/my-area/training/gym/gym-stories/gym-stories.component').then((m) => m.GymStoriesComponent),
    },
    {
        path: 'pregnancy-stories/:programId/:cardId',
        canActivate: [authGuard],
        data: { gymPlanType: 'pregnancy_rookie' },
        loadComponent: () => import('./features/my-area/training/gym/gym-stories/gym-stories.component').then((m) => m.GymStoriesComponent),
    },
    {
        path: 'postpartum-stories/:programId/:cardId',
        canActivate: [authGuard],
        data: { gymPlanType: 'postpartum' },
        loadComponent: () => import('./features/my-area/training/gym/gym-stories/gym-stories.component').then((m) => m.GymStoriesComponent),
    },
];
